import { FileEntity } from '@/shared/interfaces';
import { useReducer, useCallback } from 'react';

type UseFilesProps = {
    initialFiles?: FileEntity[];
    maxFileSizeMb?: number;
    maxFileCount?: number;
    maxTotalSizeMb?: number;
};

type AppendAction = {
    type: 'append';
    payload: FileEntity[];
};
type SetUploadedAction = {
    type: 'setUploaded';
    payload: { identifier: string; guid: string };
};
type SetFailedAction = { type: 'setFailed'; payload: string };
type RemoveAction = { type: 'remove'; payload: string };

type Action =
    | AppendAction
    | SetUploadedAction
    | SetFailedAction
    | RemoveAction
    | { type: 'reset' };

const reducer = (state: FileEntity[], action: Action): FileEntity[] => {
    switch (action.type) {
        case 'reset':
            return [];
        case 'append': {
            // append only if we have new entities
            return action.payload.length > 0
                ? [...state, ...action.payload]
                : state;
        }
        case 'setUploaded': {
            let changed = false;
            const next = state.map((f) => {
                if (
                    f.identifier === action.payload.identifier &&
                    !f.isUploaded
                ) {
                    changed = true;
                    return {
                        ...f,
                        isPending: false,
                        isUploaded: true,
                        guid: action.payload.guid,
                        s3Key: `${action.payload.guid}/${f.file.name}`,
                    };
                }
                return f;
            });
            return changed ? next : state;
        }
        case 'setFailed': {
            let changed = false;
            const next = state.map((f) => {
                if (f.identifier === action.payload && !f.isFailed) {
                    changed = true;
                    return { ...f, isPending: false, isFailed: true };
                }
                return f;
            });
            return changed ? next : state;
        }
        case 'remove': {
            const exists = state.some((f) => f.identifier === action.payload);
            return exists
                ? state.filter((f) => f.identifier !== action.payload)
                : state;
        }
        default:
            return state;
    }
};

export const useFiles = (props?: UseFilesProps) => {
    const [files, dispatch] = useReducer(reducer, props?.initialFiles ?? []);

    const appendFiles = useCallback(
        (fileList: FileList) => {
            const maxBytes = props?.maxFileSizeMb
                ? props.maxFileSizeMb * 1024 * 1024
                : undefined;

            const maxTotalBytes = props?.maxTotalSizeMb
                ? props.maxTotalSizeMb * 1024 * 1024
                : undefined;
            let remainingTotalBytes =
                maxTotalBytes === undefined
                    ? undefined
                    : maxTotalBytes -
                      files.reduce((total, file) => total + file.file.size, 0);
            let remainingCount =
                props?.maxFileCount === undefined
                    ? Number.POSITIVE_INFINITY
                    : Math.max(props.maxFileCount - files.length, 0);

            const newEntities: FileEntity[] = [];
            for (const file of Array.from(fileList)) {
                if (
                    remainingCount <= 0 ||
                    (maxBytes !== undefined && file.size > maxBytes)
                ) {
                    continue;
                }
                if (
                    remainingTotalBytes !== undefined &&
                    file.size > remainingTotalBytes
                ) {
                    continue;
                }

                newEntities.push({
                    identifier: `${file.name}-${file.size}-${Date.now()}`,
                    file,
                    fileName: file.name,
                    isPending: true, // mark pending immediately
                    isUploaded: false,
                    isFailed: false,
                    s3Key: '',
                });
                remainingCount -= 1;
                if (remainingTotalBytes !== undefined) {
                    remainingTotalBytes -= file.size;
                }
            }

            dispatch({ type: 'append', payload: newEntities });
            return newEntities;
        },
        [
            dispatch,
            files,
            props?.maxFileCount,
            props?.maxFileSizeMb,
            props?.maxTotalSizeMb,
        ],
    );

    const setUploaded = useCallback(
        (payload: { identifier: string; guid: string }) => {
            dispatch({ type: 'setUploaded', payload });
        },
        [dispatch],
    );

    const setFailed = useCallback(
        (identifier: string) => {
            dispatch({ type: 'setFailed', payload: identifier });
        },
        [dispatch],
    );

    const removeFile = useCallback(
        (identifier: string) => {
            dispatch({ type: 'remove', payload: identifier });
        },
        [dispatch],
    );

    const resetFiles = useCallback(() => {
        dispatch({ type: 'reset' });
    }, [dispatch]);

    return {
        files,
        resetFiles,
        appendFiles,
        setUploaded,
        setFailed,
        removeFile,
    };
};
